import { z } from 'zod';

/**
 * Component catalog contracts (schema-first, ADR-0008).
 *
 * These mirror what `apps/api` serialises off `GET /api/components` and
 * `POST /api/components` — its `domain/component.ts`, as it comes over the wire.
 * Parsed, not cast, for the reason `feature-flags/schemas/flag.ts` gives: this
 * is a network boundary, and a drifted response should fail loudly here rather
 * than render as `undefined` three components deep.
 *
 * ## What a component is, restated because the word is overloaded in this app
 *
 * A component is a *bundle of entitlement flags* — the middle layer between a
 * flag and a product, the thing Product names and Ops activates against a
 * museum. It is not the React kind, and it is not the client-side "Components"
 * tab under `features/clients/`, which answers what one museum has. This row
 * answers what exists to be sold.
 *
 * It holds no activation state on purpose. Whether a given museum has this
 * component is a fact about that museum's override rows and their `grantedBy`,
 * never a fact about this row — which is why there is no `enabled` here to
 * render, and why creating one turns nothing on for anybody.
 */

/**
 * Members, in the order an operator arranged them.
 *
 * An array rather than a set, matching the API: the order is editorial and
 * worth keeping, and an empty list is a legitimate state — a component may be
 * assembled before the flags it will hold exist.
 */
const flagKeys = z.array(z.string());

export const Component = z.object({
    id: z.string(),
    displayName: z.string(),
    description: z.string(),
    /** The team accountable for the capability, not for the catalog row. */
    owner: z.string(),
    flagKeys,
    /**
     * Where this component's own service answers an activation request, and the
     * path on it. Absent for the many components that need no provisioning at
     * all, which is why they are optional rather than defaulted to `''`:
     * "nothing to ask" and "ask this empty address" are different facts.
     *
     * Nothing in this slice calls either. They are displayed so the catalog can
     * describe a component fully before activation exists to use them.
     */
    ownerService: z.string().optional(),
    activationPath: z.string().optional(),
    createdAt: z.string(),
    createdBy: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    /** Optimistic-concurrency token, as on a flag definition. Required on every later write. */
    version: z.number().int(),
});
export type Component = z.infer<typeof Component>;

export const ComponentListResponse = z.object({ components: z.array(Component) });
